/* ----------------------------------------
   Upload helpers. Every product's config (GET /api/products/:id/upload-config,
   set up in Banks & Products) says which Excel column holds each system field.
---------------------------------------- */

const pad = (n) => String(n).padStart(2, "0");
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
// Column names match ignoring capitals, spaces, underscores, dots and dashes ("Customer Name" = "CUSTOMER_NAME")
const normalizeHeader = (header) => String(header).toLowerCase().replace(/[^a-z0-9]/g, "");

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

// Excel date cell (Date) or text in the product's date format → { day, month, year }
export const parseDate = (value, dateFormat) => {
  if (value instanceof Date) {
    // SheetJS dates can be a few seconds before midnight; shift to midday before reading the day
    const d = new Date(value.getTime() + 12 * 60 * 60 * 1000);
    return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
  }

  const text = String(value).trim();
  let day;
  let month;
  let year;

  // Month written as a name ("15-Mar-2023", "15 March 2023") is read the same for every date format
  const named = text.match(/^(\d{1,2})[-\s/.]([A-Za-z]{3,9})[-\s/.,]+(\d{4})$/);
  if (named) {
    day = Number(named[1]);
    month = MONTHS.indexOf(named[2].slice(0, 3).toLowerCase()) + 1;
    year = Number(named[3]);
    if (!month) return null;
  } else {
    const parts = text.split(/[-/.]/);
    const order = dateFormat.split(/[-/.]/);
    if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
    if (parts[order.indexOf("YYYY")].length !== 4) return null;

    const get = (token) => Number(parts[order.indexOf(token)]);
    day = get("DD");
    month = get("MM");
    year = get("YYYY");
  }

  const check = new Date(year, month - 1, day);
  if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) {
    return null;
  }
  return { day, month, year };
};

// Text shown in the preview table and read back when building records
export const formatDate = ({ day, month, year }, dateFormat) =>
  dateFormat.replace("DD", pad(day)).replace("MM", pad(month)).replace("YYYY", year);

// Case dates go to the backend as MM/DD/YYYY (parsed by toDateOnly).
// Loan/vehicle dates are stored as text as the Excel shows them (e.g. 18.06.2022); a month-first
// format is stored day-first instead, because the backend reads stored dates day-first (parseStoredDate).
const toCaseDate = ({ day, month, year }) => `${pad(month)}/${pad(day)}/${year}`;
const detailDateFormat = (dateFormat) => (dateFormat.startsWith("MM") ? "DD-MM-YYYY" : dateFormat);

// Rows (array of objects) → .xlsx Blob, for the error Excels
export const toExcelBlob = (XLSX, rows, header) => {
  const worksheet = header ? XLSX.utils.aoa_to_sheet([header, ...rows]) : XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
  return new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" });
};

// Excel rows keyed by header, with raw cell values like the old Axis uploader read them
// (numbers stay numbers, e.g. 500000 for a cell shown as "5,00,000.00").
// Real date cells are written in the product's date format so they read back the same way.
// Pass the workbook read with { cellDates: true }.
// Rows with nothing in them (e.g. formatted but empty rows below the data) are left out.
export const readSheetRows = (XLSX, sheet, dateFormat) =>
  XLSX.utils
    .sheet_to_json(sheet, { defval: "", raw: true })
    .filter((row) => Object.values(row).some((value) => !isBlank(value)))
    .map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          value instanceof Date ? formatDate(parseDate(value, dateFormat), dateFormat) : value,
        ]),
      ),
    );

// Map each configured field to the actual header in the uploaded Excel (see normalizeHeader)
export const resolveColumns = (excelHeaders, config) => {
  const byName = new Map(excelHeaders.map((h) => [normalizeHeader(h), h]));
  const resolved = {};
  const missing = [];

  config.columns.forEach((column) => {
    const header = byName.get(normalizeHeader(column.excelColumn));
    if (header === undefined) {
      missing.push(column);
    } else {
      resolved[column.field] = { ...column, header };
    }
  });

  return { resolved, missing };
};

/* ----------------------------------------
   Build one upload record per case.
   Consecutive rows with the same Reference No are one case:
   the first row is the borrower (B), the rest co-borrowers (C).
---------------------------------------- */
export const buildCases = (rows, config, bankId, productId) => {
  if (!rows.length) return { cases: [], errors: [] };

  const { resolved } = resolveColumns(Object.keys(rows[0]), config);
  const fieldsOf = (group) => Object.values(resolved).filter((c) => c.group === group);

  const read = (row, column) => {
    const value = row[column.header];
    if (isBlank(value)) return undefined;
    if (column.type === "date") {
      const date = parseDate(value, config.dateFormat);
      return (
        date && (column.group === "CASE" ? toCaseDate(date) : formatDate(date, detailDateFormat(config.dateFormat)))
      );
    }
    return String(value).trim();
  };

  const pick = (row, group) =>
    fieldsOf(group).reduce((acc, column) => {
      const value = read(row, column);
      if (value !== undefined) acc[column.field] = value;
      return acc;
    }, {});

  const refColumn = resolved.Reference_no;
  const cases = [];
  const errors = [];
  const seenRefs = new Map();

  rows.forEach((row, index) => {
    const refNo = String(row[refColumn.header]).trim();
    const current = cases[cases.length - 1];

    if (current && current.refNo === refNo) {
      current.rows.push(row);
      return;
    }

    if (seenRefs.has(refNo)) {
      errors.push({
        RowNo: index + 2,
        Reference_no: refNo,
        ErrorMessage: `Reference No also used at row ${seenRefs.get(refNo) + 2}; keep rows of one case together`,
      });
      return;
    }

    seenRefs.set(refNo, index);
    cases.push({ refNo, rows: [row], firstIndex: index });
  });

  const built = cases.map(({ refNo, rows: caseRows, firstIndex }) => {
    const first = caseRows[0];
    const borrowers = caseRows.map((row, i) => {
      const borrower = pick(row, "BORROWER");
      const type = String(borrower.Type || "").toUpperCase();
      return { ...borrower, Type: type === "B" || type === "C" ? type : i === 0 ? "B" : "C" };
    });

    const record = {
      ...pick(first, "CASE"),
      Client_id: bankId,
      Product_id: productId,
      Uploaded_by: "Admin",
      Borrower: borrowers,
    };

    if (config.detailType === "LOAN") {
      record.axis_loan = { ...pick(first, "LOAN"), accno: record.Acc_no, name: borrowers[0]?.Cust_name || "" };
    } else if (config.detailType === "VEHICLE") {
      record.Axis_Cv = pick(first, "VEHICLE");
    }

    return { refNo, rowNo: firstIndex + 2, record };
  });

  return { cases: built, errors };
};
